import React, { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsap';

const HERO_VIDEO_MP4_URL = 'https://tafworldwide.b-cdn.net/hero-bg-h264.mp4';
const HERO_VIDEO_WEBM_URL = 'https://tafworldwide.b-cdn.net/WEBSITE%20PAGE_1_chr2_prob4.webm';
const HERO_LOGO_URL = 'https://tafworldwide.b-cdn.net/TAF3%20LIQUID%20CHROME.png';

// Track Contact event for Meta Pixel
const trackWhatsAppClick = () => {
  if (typeof window.fbq === 'function') {
    const eventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    window.fbq('track', 'Contact', { 
      content_name: 'WhatsApp Chat',
      content_category: 'Hero Section',
      eventID: eventId
    });
    console.log('[Meta Pixel] Contact tracked from Hero Section', { eventId });
  }
};

export const Hero = () => {
  const sectionRef = useRef(null);

  const scrollToPortfolio = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const ctx = gsap.context(() => {
      const section = sectionRef.current;

      // ── Background parallax (scroll-out: bg drifts down as you scroll away) ──
      gsap.to('[data-gs="hero-bg"]', {
        yPercent: 25,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });

      // ── Headline logo scales/fades on scroll-out ──
      gsap.to('[data-gs="hero-logo"]', {
        scale: 0.92,
        autoAlpha: 0.4,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '60% top',
          scrub: 1,
        },
      });

      // ── Tagline + button fade-lift on scroll-out ──
      gsap.to('[data-gs="hero-sub"]', {
        y: -40,
        autoAlpha: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: '15% top',
          end: '50% top',
          scrub: 1,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden"
      style={{ background: '#000000' }}
    >
      {/* Background video with parallax */}
      <div data-gs="hero-bg" className="absolute inset-0 will-change-transform">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
          data-testid="hero-bg-video"
        >
          <source src={HERO_VIDEO_MP4_URL} type="video/mp4" />
          <source src={HERO_VIDEO_WEBM_URL} type="video/webm" />
        </video>
        {/* Subtle pure-black overlay: flat base for text readability + light top/bottom gradients. No color tint. */}
        <div className="absolute inset-0 pointer-events-none bg-black/25" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 15%)' }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.85) 100%)' }}
        />
      </div>

      {/*
        Content wrapper: top block (overline+logo) pinned near top with safe
        offset below the fixed navbar (h-20 = 80px + ~28px buffer = pt-28),
        bottom block (CTA) pinned near bottom with safe offset above the
        fixed floating WhatsApp widget (60px + 28px offset = 88px, so
        pb-32 = 128px gives a clear ~40px buffer). justify-between keeps the
        vertical middle of the viewport empty so the video's center stays visible.
      */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-between px-4 pt-28 pb-32">
        {/* Top: Overline + Logo */}
        <div className="text-center">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.4em] text-orange-400 mb-6">
            TAF WORLDWIDE CINEMATOGRAPHY
          </p>

          {/* Headline logo (replaces CINEMATOGRAPHER text) */}
          <div data-gs="hero-logo" className="will-change-transform flex justify-center">
            <img
              src={HERO_LOGO_URL}
              alt="TAF Worldwide Cinematographer"
              className="w-[min(85vw,900px)] h-auto select-none"
              draggable={false}
              data-testid="hero-headline-logo"
            />
          </div>
        </div>

        {/* Bottom: CTA — spread left/right, scales up for landscape/desktop */}
        <div data-gs="hero-sub" className="will-change-transform w-full">
          <div className="flex items-center justify-center gap-6 sm:gap-12 md:gap-16 lg:gap-20 w-full max-w-[560px] md:max-w-2xl mx-auto">
            <button
              onClick={scrollToPortfolio}
              data-testid="hero-view-work-btn"
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 md:px-12 py-3.5 sm:py-4 md:py-5 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white font-bold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30 text-sm sm:text-base md:text-lg tracking-wide whitespace-nowrap"
            >
              View Our Work
            </button>
            <a
              href="https://wa.me/601133131844?text=Hi%20TAF%20Worldwide!%20I%20am%20interested%20in%20your%20cinematography%20services."
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackWhatsAppClick}
              data-testid="hero-whatsapp-btn"
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 sm:gap-2.5 px-5 sm:px-8 md:px-10 py-3.5 sm:py-4 md:py-5 text-white font-bold rounded-full transition-all duration-300 hover:scale-105 text-xs sm:text-sm md:text-base tracking-wide border border-white/15 hover:border-green-500/50 bg-white/5 hover:bg-green-500/10 whitespace-nowrap"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              Chat WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Scroll hint arrow */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-orange-400/80 animate-bounce z-10">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
};
